/**
 * @file DataContext.jsx
 * Contesto dati dell'applicazione: centralizza tutti i dati di lookup e anagrafica.
 *
 * Gestisce tramite useReducer: planningHeaders, clienti, anagrafica, BU, sedi,
 * utenti, aree, giorni non lavorativi, deadlines, task, assignments e patterns.
 * Espone loadAll/refreshEntity per ricaricamenti parziali e funzioni CRUD per ogni entità.
 * Calcola lookup maps (buildLookupMaps) e aggregati ore giornalieri (buildResourceDayAggregates)
 * ogni volta che i dati cambiano.
 */

import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { AppContext } from "./AppContext";
import { buildLookupMaps } from "../lib/utils/lookup";
import { buildResourceDayAggregates } from "../lib/utils/aggregates";
import {
  fetchHeaders,
  fetchClienti,
  fetchAnagrafica,
  saveAnagrafica,
  deleteAnagraficaRecord,
  saveCliente,
  deleteClienteRecord,
  fetchBusinessUnits,
  saveBusinessUnit,
  deleteBusinessUnit,
  fetchSedi,
  saveSede,
  deleteSede,
  fetchAree,
  saveArea,
  deleteArea,
  softDeleteArea,
  softDeleteBusinessUnit,
  softDeleteSede,
  fetchNonWorkingDays,
  saveNonWorkingDay,
  deleteNonWorkingDay,
  fetchDeadlines,
  saveDeadline,
  deleteDeadline,
} from "../lib/api/pianoGiorno";

function normalizeAnagraficaItem(a) {
  return {
    id: a.id,
    consulente: a.consulente ?? '',
    area: a.area ?? '',
    bu: a.businessUnit?.descrizione ?? a.businessUnit?.buCode ?? (typeof a.businessUnit === 'string' ? a.businessUnit : '') ?? '',
    buCode: a.businessUnit?.buCode ?? (typeof a.businessUnit === 'string' ? a.businessUnit : '') ?? '',
    sede: typeof a.sede === 'object' ? (a.sede?.nome ?? '') : (a.sede ?? ''),
    flEsterno: a.flEsterno ?? false,
    flAttivo: a.flAttivo !== false,
    flDummy: a.flDummy ?? false,
    matricola: a.matricola ?? '',
  };
}

/**
 * Normalizza un header proveniente dal BE reale nel formato interno FE.
 * Campi BE (tabella tb_pianificazione_header): { id_header, pm, note, stato, mese, anno }
 */
function normalizeHeader(h) {
  let dataFinePianificazione = null;
  if (h.dataFinePianificazione) {
    const raw = h.dataFinePianificazione;
    // Stringa ISO "YYYY-MM-DD" pura: estrai direttamente per evitare lo shift
    // di timezone che si avrebbe con new Date("YYYY-MM-DD") → midnight UTC.
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      dataFinePianificazione = raw;
    } else {
      // Timestamp numerico o stringa ISO con orario/timezone: usa metodi locali.
      const d = new Date(raw);
      if (!isNaN(d)) {
        const pad = (n) => String(n).padStart(2, "0");
        dataFinePianificazione = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
      }
    }
  }
  return {
    id: h.idHeader ?? h.id_header ?? h.id,
    project_manager_id: h.pm,
    request_month: Number(h.mese),
    request_year: Number(h.anno),
    progress_status: h.stato,
    general_notes: h.note ?? "",
    dataFinePianificazione,
  };
}

async function fetchRealHeaders() {
  const items = await fetchHeaders();
  return items.map(normalizeHeader);
}

export const DataContext = createContext(null);

const ENTITY_TO_STATE_KEY = {
  planning_headers: "planningHeaders",
  planning_consultants: "planningConsultants",
  planning_calendar: "planningCalendar",
  clienti: "clienti",
  risorse: "risorse",
  aree: "aree",
  business_units: "businessUnits",
  sedi: "sedi",
  non_working_days: "nonWorkingDays",
  date_limite_pianificazioni: "dateLimitePianificazioni",
};

const initialState = {
  planningHeaders: [],
  planningConsultants: [],
  planningCalendar: [],
  groupMembers: [],
  risorse: [],
  clienti: [],
  aree: [],
  businessUnits: [],
  sedi: [],
  nonWorkingDays: [],
  dateLimitePianificazioni: [],
  projects: [],
  tasks: [],
  taskResourceLinks: [],
  taskResourcePatterns: [],

  lookupMaps: {
    risorseByCode: {},
    areeByCode: {},
    buByCode: {},
    clientiByCode: {},
    sediByCode: {},
    nwdByDate: {},
    deadlineByArea: {},
  },

  resourceDayAggregates: {},
  isLoading: false,
  loadError: null,
  lastLoaded: null,
};

function dataReducer(state, action) {
  switch (action.type) {
    case "LOAD_START":
      return { ...state, isLoading: true, loadError: null };

    case "LOAD_SUCCESS": {
      const newState = { ...state, isLoading: false, lastLoaded: Date.now() };
      for (const [entity, data] of Object.entries(action.payload)) {
        const key = ENTITY_TO_STATE_KEY[entity];
        if (key) newState[key] = data;
      }
      if (Array.isArray(action.realHeaders))
        newState.planningHeaders = action.realHeaders;
      if (Array.isArray(action.realClienti))
        newState.clienti = action.realClienti;
      if (Array.isArray(action.realAnagrafica))
        newState.risorse = action.realAnagrafica;
      if (Array.isArray(action.realBusinessUnits))
        newState.businessUnits = action.realBusinessUnits.filter((r) => r.flAttivo !== 0);
      if (Array.isArray(action.realSedi))
        newState.sedi = action.realSedi.filter((r) => r.flAttivo !== 0);
      if (Array.isArray(action.realAree))
        newState.aree = action.realAree.filter((r) => r.flAttivo !== 0);
      if (Array.isArray(action.realNonWorkingDays))
        newState.nonWorkingDays = action.realNonWorkingDays;
      if (Array.isArray(action.realDeadlines))
        newState.dateLimitePianificazioni = action.realDeadlines;

      newState.lookupMaps = buildLookupMaps(newState);
      newState.resourceDayAggregates = buildResourceDayAggregates(
        newState.planningCalendar,
      );
      return newState;
    }

    case "LOAD_ERROR":
      return { ...state, isLoading: false, loadError: action.error };

    case "UPDATE_ENTITY": {
      const key = ENTITY_TO_STATE_KEY[action.entity];
      if (!key) return state;
      const newState = { ...state, [key]: action.data };
      newState.lookupMaps = buildLookupMaps(newState);
      if (action.entity === "planning_calendar") {
        newState.resourceDayAggregates = buildResourceDayAggregates(
          newState.planningCalendar,
        );
      }
      return newState;
    }

    default:
      return state;
  }
}

export function DataProvider({ children }) {
  const { userName } = useContext(AppContext) ?? {};
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const hasFetched = useRef(false);

  const loadAll = useCallback(async () => {
    dispatch({ type: "LOAD_START" });
    try {
      const allFetches = [
        fetchRealHeaders()
          .then((data) => ({ entity: "__real_headers__", data }))
          .catch(() => ({ entity: "__real_headers__", data: null })),
        fetchClienti()
          .then((data) => ({ entity: "__real_clienti__", data }))
          .catch(() => ({ entity: "__real_clienti__", data: null })),
        fetchAnagrafica()
          .then((data) => ({ entity: "__real_anagrafica__", data }))
          .catch(() => ({ entity: "__real_anagrafica__", data: null })),
        fetchBusinessUnits()
          .then((data) => ({ entity: "__real_business_units__", data }))
          .catch(() => ({ entity: "__real_business_units__", data: null })),
        fetchSedi()
          .then((data) => ({ entity: "__real_sedi__", data }))
          .catch(() => ({ entity: "__real_sedi__", data: null })),
        fetchAree()
          .then((data) => ({ entity: "__real_aree__", data }))
          .catch(() => ({ entity: "__real_aree__", data: null })),
        fetchNonWorkingDays()
          .then((data) => ({ entity: "__real_non_working_days__", data }))
          .catch(() => ({ entity: "__real_non_working_days__", data: null })),
        fetchDeadlines()
          .then((data) => ({ entity: "__real_deadlines__", data }))
          .catch(() => ({ entity: "__real_deadlines__", data: null })),
      ];
      const results = await Promise.allSettled(allFetches);
      const payload = {};
      let realHeaders = null;
      let realClienti = null;
      let realAnagrafica = null;
      let realBusinessUnits = null;
      let realSedi = null;
      let realAree = null;
      let realNonWorkingDays = null;
      let realDeadlines = null;
      for (const result of results) {
        if (result.status !== "fulfilled") continue;
        const { entity, data } = result.value;
        if (entity === "__real_headers__") {
          realHeaders = data;
        } else if (entity === "__real_clienti__") {
          realClienti = data;
        } else if (entity === "__real_anagrafica__") {
          realAnagrafica = Array.isArray(data) ? data.map(normalizeAnagraficaItem) : null;
        } else if (entity === "__real_business_units__") {
          realBusinessUnits = data;
        } else if (entity === "__real_sedi__") {
          realSedi = data;
        } else if (entity === "__real_aree__") {
          realAree = data;
        } else if (entity === "__real_non_working_days__") {
          realNonWorkingDays = data;
        } else if (entity === "__real_deadlines__") {
          realDeadlines = data;
        } else {
          const key = ENTITY_TO_STATE_KEY[entity];
          if (key) payload[key] = data;
        }
      }
      dispatch({
        type: "LOAD_SUCCESS",
        payload,
        realHeaders,
        realClienti,
        realAnagrafica,
        realBusinessUnits,
        realSedi,
        realAree,
        realNonWorkingDays,
        realDeadlines,
      });
    } catch (error) {
      dispatch({ type: "LOAD_ERROR", error: error.message });
    }
  }, []);

  const refreshEntity = useCallback(async (entity) => {
    if (entity === "planning_headers") {
      const data = await fetchRealHeaders().catch(() => null);
      if (data) dispatch({ type: "UPDATE_ENTITY", entity: "planning_headers", data });
      return;
    }
    if (entity === "risorse") {
      const raw = await fetchAnagrafica().catch(() => null);
      const data = Array.isArray(raw) ? raw.map(normalizeAnagraficaItem) : [];
      dispatch({ type: "UPDATE_ENTITY", entity: "risorse", data });
      return;
    }
    if (entity === "clienti") {
      const data = await fetchClienti().catch(() => null);
      if (data) dispatch({ type: "UPDATE_ENTITY", entity: "clienti", data });
      return;
    }
    if (entity === "business_units") {
      const raw = await fetchBusinessUnits().catch(() => null);
      if (raw) dispatch({ type: "UPDATE_ENTITY", entity: "business_units", data: raw.filter((r) => r.flAttivo !== 0) });
      return;
    }
    if (entity === "sedi") {
      const raw = await fetchSedi().catch(() => null);
      if (raw) dispatch({ type: "UPDATE_ENTITY", entity: "sedi", data: raw.filter((r) => r.flAttivo !== 0) });
      return;
    }
    if (entity === "aree") {
      const raw = await fetchAree().catch(() => null);
      if (raw) dispatch({ type: "UPDATE_ENTITY", entity: "aree", data: raw.filter((r) => r.flAttivo !== 0) });
      return;
    }
    if (entity === "non_working_days") {
      const data = await fetchNonWorkingDays().catch(() => null);
      if (data) dispatch({ type: "UPDATE_ENTITY", entity: "non_working_days", data });
      return;
    }
    if (entity === "date_limite_pianificazioni") {
      const data = await fetchDeadlines().catch(() => null);
      if (data) dispatch({ type: "UPDATE_ENTITY", entity: "date_limite_pianificazioni", data });
      return;
    }
  }, []);

  const createRecord = useCallback(
    async (entity, body) => {
      if (entity === "risorse") {
        await saveAnagrafica(body);
      } else if (entity === "clienti") {
        const ts = Date.now();
        await saveCliente({ codice: body.codice, nome: body.nome, gruppo: body.gruppo, dataIns: ts, utenteIns: userName || null, dataAgg: ts, utenteAgg: userName || null });
      } else if (entity === "business_units") {
        const ts = Date.now();
        await saveBusinessUnit({ ...body, flAttivo: true, dataIns: ts, utenteIns: userName || null, dataAgg: ts, utenteAgg: userName || null });
      } else if (entity === "sedi") {
        await saveSede({ ...body, flAttivo: true });
      } else if (entity === "aree") {
        await saveArea({ ...body, flAttivo: true });
      } else if (entity === "non_working_days") {
        await saveNonWorkingDay(body);
      } else if (entity === "date_limite_pianificazioni") {
        await saveDeadline(body);
      }
      await refreshEntity(entity);
    },
    [refreshEntity],
  );

  const updateRecord = useCallback(
    async (entity, pkValue, body) => {
      if (entity === "risorse") {
        await saveAnagrafica({ ...body, id: pkValue });
      } else if (entity === "clienti") {
        const ts = Date.now();
        await saveCliente({ codice: pkValue, nome: body.nome, gruppo: body.gruppo, dataAgg: ts, utenteAgg: userName || null });
      } else if (entity === "business_units") {
        const ts = Date.now();
        await saveBusinessUnit({ ...body, id: pkValue, dataAgg: ts, utenteAgg: userName || null });
      } else if (entity === "sedi") {
        await saveSede({ ...body, idSede: pkValue });
      } else if (entity === "aree") {
        await saveArea({ ...body, id: pkValue });
      } else if (entity === "non_working_days") {
        await saveNonWorkingDay({ ...body, id: pkValue });
      } else if (entity === "date_limite_pianificazioni") {
        await saveDeadline({ ...body, id: pkValue });
      }
      await refreshEntity(entity);
    },
    [refreshEntity],
  );

  const removeRecord = useCallback(
    async (entity, pkValue) => {
      if (entity === "risorse") {
        await deleteAnagraficaRecord(pkValue);
      } else if (entity === "clienti") {
        await deleteClienteRecord(pkValue);
      } else if (entity === "business_units") {
        await softDeleteBusinessUnit(pkValue);
      } else if (entity === "sedi") {
        await softDeleteSede(pkValue);
      } else if (entity === "aree") {
        await softDeleteArea(pkValue);
      } else if (entity === "non_working_days") {
        await deleteNonWorkingDay(pkValue);
      } else if (entity === "date_limite_pianificazioni") {
        await deleteDeadline(pkValue);
      }
      await refreshEntity(entity);
    },
    [refreshEntity],
  );

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadAll();
  }, [loadAll]);

  return (
    <DataContext.Provider
      value={{
        ...state,
        loadAll,
        refreshEntity,
        createRecord,
        updateRecord,
        removeRecord,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}
